import { Controller } from "@hotwired/stimulus";
import { FloatingTooltipManager } from "../lib/floating_tooltip";

type HtmxConversationSwapEvent = Event & {
    detail?: {
        requestConfig?: {
            path?: string;
        };
        target?: EventTarget | null;
    };
};

export default class extends Controller<HTMLElement> {
    static targets = ["input", "scroll", "sessions", "conversation", "sessionLink"];
    static values = { sessionsUrl: String };

    declare readonly inputTarget: HTMLTextAreaElement;
    declare readonly hasInputTarget: boolean;
    declare readonly hasScrollTarget: boolean;
    declare readonly scrollTarget: HTMLElement;
    declare readonly hasSessionsTarget: boolean;
    declare readonly sessionsTarget: HTMLElement;
    declare readonly hasConversationTarget: boolean;
    declare readonly conversationTarget: HTMLElement;
    declare readonly sessionLinkTargets: HTMLAnchorElement[];
    declare readonly sessionsUrlValue: string;
    declare readonly hasSessionsUrlValue: boolean;

    private mediaQuery: MediaQueryList | null = null;
    private tooltipManager: FloatingTooltipManager | null = null;
    private sessionsRefreshInFlight = false;
    private sessionsRefreshQueued = false;
    private readonly activeSessionClasses = [
        "bg-card",
        "text-foreground",
        "ticket-card-selected",
    ];
    private readonly inactiveSessionClasses = ["text-muted-foreground"];

    connect(): void {
        this.syncMobilePane = this.syncMobilePane.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
        this.handleHtmxHistory = this.handleHtmxHistory.bind(this);
        this.handleRenderedUpdate = this.handleRenderedUpdate.bind(this);
        this.ensureConversationHistoryAfterSwap =
            this.ensureConversationHistoryAfterSwap.bind(this);
        this.mediaQuery = window.matchMedia("(max-width: 1023.98px)");
        this.mediaQuery.addEventListener("change", this.syncMobilePane);
        window.addEventListener("popstate", this.handlePopState);
        document.body.addEventListener("htmx:pushedIntoHistory", this.handleHtmxHistory);
        document.body.addEventListener("htmx:replacedInHistory", this.handleHtmxHistory);
        document.addEventListener("htmx:afterSwap", this.handleRenderedUpdate);
        document.addEventListener("turbolinks:load", this.handleRenderedUpdate);
        this.element.dataset.mobilePane = this.paneFromHistory();
        this.syncMobilePane();
        this.syncConversationSelection();
        this.scrollToBottom();
        this.tooltipManager = new FloatingTooltipManager(
            this.element,
            "[data-global-assistant-tooltip-trigger]",
            "[data-global-assistant-tooltip]",
        );
        this.tooltipManager.connect();
    }

    disconnect(): void {
        this.mediaQuery?.removeEventListener("change", this.syncMobilePane);
        window.removeEventListener("popstate", this.handlePopState);
        document.body.removeEventListener("htmx:pushedIntoHistory", this.handleHtmxHistory);
        document.body.removeEventListener("htmx:replacedInHistory", this.handleHtmxHistory);
        document.removeEventListener("htmx:afterSwap", this.handleRenderedUpdate);
        document.removeEventListener("turbolinks:load", this.handleRenderedUpdate);
        this.tooltipManager?.disconnect();
        this.tooltipManager = null;
    }

    sessionsTargetConnected(): void {
        this.syncMobilePane();
        this.syncConversationSelection();
    }

    conversationTargetConnected(): void {
        this.syncMobilePane();
        this.syncConversationSelection();
    }

    showSessions(event: Event): void {
        event.preventDefault();
        this.element.dataset.mobilePane = "sessions";
        const sessionsUrl = new URL(window.location.href);
        sessionsUrl.hash = "conversations";
        window.history.pushState(
            { ...(window.history.state || {}), globalAssistantPane: "sessions" },
            "",
            sessionsUrl
        );
        this.syncMobilePane();
    }

    openConversation(event: MouseEvent): void {
        this.element.dataset.mobilePane = "conversation";
        this.syncMobilePane();
        const link = event.currentTarget as HTMLAnchorElement;
        const targetUrl = new URL(link.href);
        const opensSelectedConversation =
            targetUrl.pathname === window.location.pathname &&
            targetUrl.search === window.location.search;
        if (opensSelectedConversation) {
            event.preventDefault();
            window.history.pushState(
                { ...(window.history.state || {}), globalAssistantPane: "conversation" },
                "",
                targetUrl
            );
        } else if (this.hasConversationTarget) {
            this.conversationTarget.addEventListener(
                "htmx:afterSwap",
                this.ensureConversationHistoryAfterSwap,
                { once: true },
            );
        }
    }

    keydown(event: KeyboardEvent): void {
        if (event.key !== "Enter" || event.shiftKey || event.isComposing) return;
        event.preventDefault();
        const form = (event.target as HTMLElement).closest<HTMLFormElement>("form");
        if (form && this.inputTarget.value.trim()) form.requestSubmit();
    }

    useSuggestion(event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLElement) || !this.hasInputTarget) return;
        const suggestion = trigger.dataset.suggestion;
        if (!suggestion) return;

        event.preventDefault();
        this.inputTarget.value = suggestion;
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
        this.inputTarget.focus();
        this.inputTarget.setSelectionRange(suggestion.length, suggestion.length);
    }

    afterRequest(event: CustomEvent<{ successful?: boolean; elt?: Element }>): void {
        if (!event.detail?.successful || !this.hasInputTarget) return;
        const submittedForm =
            event.detail.elt?.closest("form") ??
            (event.target instanceof Element ? event.target.closest("form") : null);
        if (!submittedForm?.contains(this.inputTarget)) return;
        this.inputTarget.value = "";
        this.inputTarget.defaultValue = "";
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has("support")) {
            currentUrl.searchParams.delete("support");
            window.history.replaceState(window.history.state, "", currentUrl);
        }
        this.inputTarget.focus();
        this.refreshSessions();
        window.requestAnimationFrame(() => this.scrollToBottom());
    }

    afterRunMutation(event: CustomEvent<{ successful?: boolean }>): void {
        if (!event.detail?.successful) return;
        this.refreshSessions();
    }

    afterStreamDone(): void {
        this.refreshSessions();
    }

    afterArchive(event: CustomEvent<{ successful?: boolean }>): void {
        if (!event.detail?.successful) return;
        const button = event.currentTarget as HTMLElement;
        const item = button.closest<HTMLElement>('[data-testid="global-assistant-conversation-item"]');
        const archivedChatId = item
            ?.querySelector<HTMLElement>('[data-chat-id]')
            ?.dataset.chatId;
        const selectedChatId = this.element
            .querySelector<HTMLElement>('[data-testid="global-assistant-fragment"]')
            ?.dataset.chatId;
        const fallbackSessionLink = this.sessionLinkTargets.find(
            (link) => link.dataset.chatId !== archivedChatId
        );
        item?.remove();
        if (archivedChatId && archivedChatId === selectedChatId) {
            if (fallbackSessionLink) {
                fallbackSessionLink.click();
                return;
            }

            this.element
                .querySelector<HTMLButtonElement>('[data-testid="global-assistant-new-conversation"]')
                ?.click();
        }
    }

    private scrollToBottom(): void {
        if (!this.hasScrollTarget) return;
        this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight;
    }

    private refreshSessions(): void {
        if (this.sessionsRefreshInFlight) {
            this.sessionsRefreshQueued = true;
            return;
        }
        if (!this.hasSessionsUrlValue) return;

        const ajax = window.htmx?.ajax;
        const target = this.element.querySelector<HTMLElement>(
            "#global-assistant-sessions-fragment"
        );
        const selectedChatId = this.element
            .querySelector<HTMLElement>("#global-assistant-conversation-fragment")
            ?.dataset.chatId;
        if (!ajax || !target) return;

        const url = new URL(this.sessionsUrlValue, window.location.origin);
        if (selectedChatId) url.searchParams.set("selectedChatId", selectedChatId);
        const requestUrl = `${url.pathname}${url.search}`;

        this.sessionsRefreshInFlight = true;
        let request: ReturnType<typeof ajax>;
        try {
            request = ajax("GET", requestUrl, {
                source: this.element,
                target,
                swap: "morphdom",
            });
        } catch (error) {
            this.sessionsRefreshInFlight = false;
            console.warn("[global-assistant] unable to refresh sessions", error);
            return;
        }

        const finish = (): void => {
            this.sessionsRefreshInFlight = false;
            if (!this.sessionsRefreshQueued) return;
            this.sessionsRefreshQueued = false;
            this.refreshSessions();
        };
        void Promise.resolve(request as PromiseLike<unknown>).then(finish, finish);
    }

    private handlePopState(event: PopStateEvent): void {
        this.element.dataset.mobilePane = this.historyShowsSessions(event.state)
            ? "sessions"
            : "conversation";
        this.syncMobilePane();
        this.syncConversationSelection();
        this.restoreConversationFromLocation();
    }

    private handleHtmxHistory(): void {
        window.requestAnimationFrame(() => this.syncConversationSelection());
    }

    private handleRenderedUpdate(event: Event): void {
        const target = (event as HtmxConversationSwapEvent).detail?.target;
        if (this.hasConversationTarget && target === this.conversationTarget) {
            // The conversation endpoint has persisted last_viewed_at. Clear only its
            // badge, keeping the loaded session pages and their scroll position intact.
            this.sessionLinkTargets
                .filter(link => link.dataset.chatId === this.conversationTarget.dataset.chatId)
                .forEach(link => link.querySelector('[data-testid="global-assistant-conversation-unread"]')?.remove());
            // A stream-completion refresh started before the read may still be in flight.
            // Re-fetch after it finishes so its stale response cannot restore the badge.
            if (this.sessionsRefreshInFlight) this.sessionsRefreshQueued = true;
        }
        window.requestAnimationFrame(() => this.syncConversationSelection());
    }

    ensureConversationHistoryAfterSwap(event: Event): void {
        const detail = (event as HtmxConversationSwapEvent).detail;
        const target = detail?.target;
        const requestPath = detail?.requestConfig?.path || "";
        const conversationPath = requestPath.match(
            /^(\/assistant\/chats\/[0-9a-f-]+)\/fragment(?:\?.*)?$/i,
        )?.[1];
        if (
            !(target instanceof HTMLElement) ||
            target.id !== "global-assistant-conversation-fragment" ||
            !conversationPath
        ) {
            return;
        }

        if (window.location.pathname === conversationPath) {
            return;
        }

        const historyState = { ...(window.history.state || {}) };
        delete historyState.htmx;
        window.history.pushState(historyState, "", conversationPath);
        document.body.dispatchEvent(
            new CustomEvent("htmx:pushedIntoHistory", {
                bubbles: true,
                detail: { path: conversationPath },
            }),
        );
    }

    private restoreConversationFromLocation(): void {
        const conversationPath = window.location.pathname.match(
            /^(\/assistant\/chats\/[0-9a-f-]+)$/i,
        )?.[1];
        const renderedChatId = this.hasConversationTarget
            ? this.conversationTarget.dataset.chatId
            : undefined;
        const locationChatId = conversationPath?.split("/").pop();
        if (!conversationPath || !locationChatId || renderedChatId === locationChatId) {
            return;
        }

        const htmx = window.htmx;
        const target = document.getElementById("global-assistant-conversation-fragment");
        if (!htmx?.ajax || !target) {
            return;
        }

        void Promise.resolve(htmx.ajax("GET", `${conversationPath}/fragment`, {
            target,
            swap: "morphdom",
        })).catch(error => console.warn("[global-assistant] unable to restore conversation", error));
    }

    private paneFromHistory(): "sessions" | "conversation" {
        return this.historyShowsSessions(window.history.state) ? "sessions" : "conversation";
    }

    private historyShowsSessions(state: unknown): boolean {
        const historyState = state as { globalAssistantPane?: string } | null;
        return historyState?.globalAssistantPane === "sessions" || window.location.hash === "#conversations";
    }

    private syncMobilePane(): void {
        const mobile = this.mediaQuery?.matches ?? window.matchMedia("(max-width: 1023.98px)").matches;
        const showSessions = mobile && this.element.dataset.mobilePane === "sessions";

        if (this.hasSessionsTarget) {
            this.sessionsTarget.classList.toggle("hidden", mobile && !showSessions);
            this.sessionsTarget.classList.toggle("flex", mobile && showSessions);
            this.sessionsTarget.setAttribute("aria-hidden", String(mobile && !showSessions));
        }
        if (this.hasConversationTarget) {
            this.conversationTarget.classList.toggle("hidden", showSessions);
            this.conversationTarget.setAttribute("aria-hidden", String(showSessions));
        }
    }

    private syncConversationSelection(): void {
        const pathname = window.location.pathname;

        this.sessionLinkTargets.forEach((link) => {
            const active = new URL(link.href, window.location.origin).pathname === pathname;
            this.activeSessionClasses.forEach((className) =>
                link.classList.toggle(className, active)
            );
            this.inactiveSessionClasses.forEach((className) =>
                link.classList.toggle(className, !active)
            );
            link.setAttribute("aria-current", active ? "page" : "false");
        });
    }
}
